import React, { useState } from 'react';
import PropTypes from 'prop-types';

// One item card in the new Deal-terms IA.
//
// Three render states:
//   * collapsed, unconfigured → icon + title + description + "+ Configure"
//   * collapsed, configured   → icon + title + green "Configured" pill +
//                               summary chips + pencil + trash
//   * expanded                → icon + title + close (×) + body + Cancel/Done
//
// Field-level autosave is preserved: edits inside the body fire onChangeField
// immediately and the V3OfferDealTermsPane debounces the PATCH. "Done" just
// closes the panel. "Cancel" also closes (changes are already persisted).
const V3DealTermsItem = ({ item, ctx }) => {
  const [open, setOpen] = useState(false);
  const configured = item.isConfigured(ctx);
  const chips = configured ? item.summaryChips(ctx) : [];
  const body = item.renderBody(ctx);
  // Items can opt in to required-field gating by declaring isValid(ctx).
  // When absent, Save is always enabled.
  const valid = item.isValid ? item.isValid(ctx) : true;

  const handleConfigure = (e) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    item.clear(ctx);
  };

  return (
    <div className={`deal-terms-item ${open ? 'deal-terms-item--open' : ''}`}>
      <div className="deal-terms-item__row">
        <span className="deal-terms-item__icon" aria-hidden="true">
          <i className={item.icon} />
        </span>

        <div className="deal-terms-item__title-block">
          <div className="deal-terms-item__title-row">
            <span className="deal-terms-item__title">{item.title}</span>
            {configured && !open && (
              <span className="deal-terms-item__status deal-terms-item__status--configured">
                <i className="fas fa-check" aria-hidden="true" /> Configured
              </span>
            )}
          </div>
          {!configured && !open && (
            <span className="deal-terms-item__description">{item.description}</span>
          )}
          {configured && !open && chips.length > 0 && (
            <div className="deal-terms-item__chips">
              {chips.map((c, idx) => (
                <span className="deal-terms-item__chip" key={`${c.label}:${c.value}:${idx}`}>
                  {c.label && <span className="deal-terms-item__chip-label">{c.label}</span>}
                  <span className="deal-terms-item__chip-value">{c.value}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="deal-terms-item__actions">
          {!open && configured && (
            <>
              <button
                type="button"
                className="deal-terms-item__icon-btn"
                onClick={handleConfigure}
                aria-label={`Edit ${item.title}`}
                data-testid={`deal-terms-item-${item.key}-edit`}
              >
                <i className="far fa-pen" />
              </button>
              <button
                type="button"
                className="deal-terms-item__icon-btn"
                onClick={handleClear}
                aria-label={`Clear ${item.title}`}
                data-testid={`deal-terms-item-${item.key}-clear`}
              >
                <i className="far fa-trash" />
              </button>
            </>
          )}
          {!open && !configured && (
            <button
              type="button"
              className="deal-terms-item__configure-btn"
              onClick={handleConfigure}
              data-testid={`deal-terms-item-${item.key}-configure`}
            >
              <i className="fas fa-plus" /> Configure
            </button>
          )}
          {open && (
            <button
              type="button"
              className="deal-terms-item__icon-btn"
              onClick={() => setOpen(false)}
              aria-label={`Close ${item.title}`}
              data-testid={`deal-terms-item-${item.key}-close`}
            >
              <i className="far fa-times" />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="deal-terms-item__body">
          <div className="deal-terms-item__body-inner">{body}</div>
          <div className="deal-terms-item__footer">
            <button
              type="button"
              className="deal-terms-item__cancel"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`deal-terms-item__save ${valid ? '' : 'deal-terms-item__save--disabled'}`}
              onClick={() => { if (valid) setOpen(false); }}
              disabled={!valid}
            >
              <i className="far fa-check" /> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

V3DealTermsItem.propTypes = {
  item: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    icon: PropTypes.string.isRequired,
    isConfigured: PropTypes.func.isRequired,
    summaryChips: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    renderBody: PropTypes.func.isRequired,
  }).isRequired,
  ctx: PropTypes.object.isRequired,
};

export default V3DealTermsItem;
